import interstellarApi from './interstellarApi';

/**
 * Bucket agregado de selecciones de IVR (idioma + opción presionada).
 * Coincide con `IvrSelectionBucket` de Interstellar.
 */
export interface IvrSelectionBucket {
  module_id: number | null;
  module_name: string | null;
  ivr_lang: string | null;
  ivr_choice: string | null;
  count: number;
}

export interface IvrSelectionsSummaryResponse {
  total_calls: number;
  buckets: IvrSelectionBucket[];
}

export interface IvrSelectionCallItem {
  uuid: string;
  caller_id_number: string | null;
  destination_number: string | null;
  module_id: number | null;
  module_name: string | null;
  ivr_lang: string | null;
  ivr_choice: string | null;
  start_stamp: string | null;
  duration: number | null;
  hangup_cause: string | null;
  answered: boolean;
}

export interface IvrSelectionCallsListResponse {
  total_count: number;
  page: number;
  page_size: number;
  calls: IvrSelectionCallItem[];
}

export interface FetchIvrSelectionsOptions {
  moduleId?: number;
  dateFrom?: string;
  dateTo?: string;
  includeNonIvr?: boolean;
}

export interface FetchIvrSelectionCallsOptions extends FetchIvrSelectionsOptions {
  page?: number;
  pageSize?: number;
  q?: string;
}

/**
 * `GET /freeswitch/ivr-selections/summary`
 *
 * Devuelve el conteo de llamadas agrupadas por (módulo, idioma, opción presionada).
 * Por defecto Interstellar limita a los últimos 90 días si no se proveen fechas.
 */
export const fetchIvrSelectionsSummary = async (
  accountId: number,
  options?: FetchIvrSelectionsOptions
): Promise<IvrSelectionsSummaryResponse> => {
  const response = await interstellarApi.get<IvrSelectionsSummaryResponse>(
    '/freeswitch/ivr-selections/summary',
    {
      params: {
        account_id: accountId,
        ...(options?.moduleId != null ? { module_id: options.moduleId } : {}),
        ...(options?.dateFrom ? { date_from: options.dateFrom } : {}),
        ...(options?.dateTo ? { date_to: options.dateTo } : {}),
        ...(options?.includeNonIvr ? { include_non_ivr: true } : {}),
      },
    }
  );
  return response.data;
};

/**
 * `GET /freeswitch/ivr-selections/calls`
 *
 * Lista paginada de llamadas individuales con sus selecciones de IVR.
 */
export const fetchIvrSelectionCalls = async (
  accountId: number,
  options?: FetchIvrSelectionCallsOptions
): Promise<IvrSelectionCallsListResponse> => {
  const response = await interstellarApi.get<IvrSelectionCallsListResponse>(
    '/freeswitch/ivr-selections/calls',
    {
      params: {
        account_id: accountId,
        page: options?.page ?? 1,
        page_size: options?.pageSize ?? 20,
        ...(options?.moduleId != null ? { module_id: options.moduleId } : {}),
        ...(options?.dateFrom ? { date_from: options.dateFrom } : {}),
        ...(options?.dateTo ? { date_to: options.dateTo } : {}),
        ...(options?.q ? { q: options.q } : {}),
        ...(options?.includeNonIvr ? { include_non_ivr: true } : {}),
      },
    }
  );
  return response.data;
};
